import { BaseSelectRef } from 'rc-select';
import debounce from 'lodash/debounce';
import { getDirectParent } from '@lib/popups';
import { haltKeyboardEventPropagation } from '@lib/events';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { Select } from 'antd';
import { useAppUsers } from '@hooks';

import React, { forwardRef, useMemo } from 'react';

export type AppUserSelectOption = {
	disabled: boolean;
	label: string;
	value: string;
};

export interface AppUserSelectProps {
	allowClear?: boolean;
	className?: string;
	disabled?: boolean;
	disableUsed?: boolean;
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	onChange?: (value: string) => void;
	placeholder?: string;
	popupMatchSelectWidth?: boolean;
	value?: string;
}

export const AppUserSelect = forwardRef<BaseSelectRef, AppUserSelectProps>(({
	allowClear,
	className,
	disabled,
	disableUsed,
	getPopupContainer,
	onChange,
	placeholder,
	popupMatchSelectWidth = true,
	value
}, ref) => {
	const { data, isRequested, request } = useAppUsers();

	const debouncedGetAppUsers = useMemo(
		() => debounce((filter: string | undefined) => request({ filter }), 300),
		[ request ]
	);

	const options: AppUserSelectOption[] | undefined = useMemo(
		() => data?.map((user) => ({
			disabled: Boolean(disableUsed) && Boolean(user.usedByApplicationId),
			label: user.username,
			value: user.rawSalesforceId
		})),
		[ data, disableUsed ]
	);

	return (
		<Select
			allowClear={ allowClear }
			className={ className }
			disabled={ disabled }
			filterOption={ false }
			getPopupContainer={ getPopupContainer ?? getDirectParent }
			onChange={ onChange }
			onKeyUp={ haltKeyboardEventPropagation }
			onSearch={ debouncedGetAppUsers }
			options={ options }
			placeholder={ placeholder || PLACEHOLDERS_COPY.APP_USER }
			popupMatchSelectWidth={ popupMatchSelectWidth }
			popupRender={ (menu) => (isRequested ? <LoadingSpinner>{ menu }</LoadingSpinner> : menu) }
			ref={ ref }
			showAction={ [ 'focus', 'click' ] }
			showSearch
			value={ value } />
	);
});
